import React, { useEffect, useState } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import AdminChrome from '@/components/AdminChrome';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { toast } from 'sonner';
import { Users, Loader2, Crown, ShieldCheck, Search, Edit2, GitBranch, Mail } from 'lucide-react';

const flattenNodes = (nodes, level = 0, acc = []) => {
  nodes.forEach((n) => {
    acc.push({ ...n, __level: level });
    if (n.children && n.children.length) flattenNodes(n.children, level + 1, acc);
  });
  return acc;
};

const OrgRow = ({ node }) => (
  <div className="flex items-start gap-3 py-2" style={{ paddingLeft: `${(node.__level || 0) * 24}px` }}>
    {(node.__level || 0) > 0 && <div className="w-3 border-l border-slate-200 h-6 -mr-2" aria-hidden />}
    <div className="w-10 h-10 rounded-full bg-gradient-to-br from-teal-100 to-emerald-100 flex items-center justify-center flex-shrink-0 text-teal-700 font-semibold">
      {(node.name || '?').slice(0, 1).toUpperCase()}
    </div>
    <div className="flex-1 min-w-0">
      <div className="flex flex-wrap items-center gap-2">
        <span className="font-medium text-slate-900 truncate" data-testid={`org-node-${node.id}`}>{node.name}</span>
        {node.is_admin && <Badge variant="outline" className="bg-amber-50 border-amber-300 text-amber-700 text-xs"><Crown className="w-3 h-3 mr-0.5" /> Admin</Badge>}
        {node.is_manager && !node.is_admin && <Badge variant="outline" className="bg-indigo-50 border-indigo-300 text-indigo-700 text-xs"><ShieldCheck className="w-3 h-3 mr-0.5" /> Manager</Badge>}
      </div>
      <p className="text-xs text-slate-500">{node.job_title}</p>
    </div>
  </div>
);

const TeamDirectoryPage = () => {
  const { api, user } = useAuth();
  const [members, setMembers] = useState([]);
  const [stats, setStats] = useState({});
  const [tree, setTree] = useState([]);
  const [unassigned, setUnassigned] = useState([]);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState('directory');
  const [query, setQuery] = useState('');
  const [managerDialog, setManagerDialog] = useState({ open: false, employee: null, manager_id: '', is_manager: false });
  const [saving, setSaving] = useState(false);
  const isAdmin = !!user?.is_admin;

  const load = async () => {
    setLoading(true);
    try {
      const [dirRes, orgRes] = await Promise.all([
        api.get('/team/directory'),
        api.get('/team/org-chart'),
      ]);
      setMembers(dirRes.data.members || []);
      setStats(dirRes.data.stats || {});
      setTree(orgRes.data.tree || []);
      setUnassigned(orgRes.data.unassigned || []);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to load team');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

  const openManagerDialog = (m) => setManagerDialog({
    open: true,
    employee: m,
    manager_id: m.manager_id || '',
    is_manager: !!m.is_manager,
  });

  const saveManager = async () => {
    setSaving(true);
    try {
      await api.patch(`/admin/team/${managerDialog.employee.id}/manager`, {
        manager_id: managerDialog.manager_id === '' ? '' : managerDialog.manager_id,
        is_manager: managerDialog.is_manager,
      });
      toast.success('Reporting line updated');
      setManagerDialog({ open: false, employee: null, manager_id: '', is_manager: false });
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  const q = query.trim().toLowerCase();
  const filtered = q
    ? members.filter((m) => (m.name || '').toLowerCase().includes(q) || (m.email || '').toLowerCase().includes(q) || (m.intern_role_label || '').toLowerCase().includes(q))
    : members;

  return (
    <AdminChrome activeSection="team">
      <div className="max-w-6xl mx-auto p-4 md:p-6 space-y-4">
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3">
          <div>
            <h1 className="text-2xl font-bold text-slate-900 flex items-center gap-2" data-testid="team-directory-title">
              <Users className="w-6 h-6 text-emerald-500" /> Team & Org Chart
            </h1>
            <p className="text-sm text-slate-500 mt-1">Meet everyone at Zazu, see who reports to whom.</p>
          </div>
          <div className="flex flex-wrap gap-2">
            <Badge variant="outline" className="text-slate-600">Total: {stats.total || 0}</Badge>
            <Badge variant="outline" className="bg-amber-50 border-amber-300 text-amber-700">Admins: {stats.admins || 0}</Badge>
            <Badge variant="outline" className="bg-indigo-50 border-indigo-300 text-indigo-700">Managers: {stats.managers || 0}</Badge>
            <Badge variant="outline" className="bg-slate-100 text-slate-700">Staff: {stats.staff || 0}</Badge>
            <Badge variant="outline" className="bg-emerald-50 border-emerald-300 text-emerald-700">Interns: {stats.interns || 0}</Badge>
          </div>
        </div>

        <div className="flex gap-2 border-b border-slate-200">
          <button
            onClick={() => setTab('directory')}
            className={`px-3 py-2 text-sm font-medium ${tab === 'directory' ? 'border-b-2 border-teal-500 text-teal-600' : 'text-slate-500'}`}
            data-testid="tab-directory"
          >
            Directory
          </button>
          <button
            onClick={() => setTab('orgchart')}
            className={`px-3 py-2 text-sm font-medium ${tab === 'orgchart' ? 'border-b-2 border-teal-500 text-teal-600' : 'text-slate-500'}`}
            data-testid="tab-orgchart"
          >
            <GitBranch className="w-4 h-4 inline mr-1" /> Org chart
          </button>
        </div>

        {loading ? (
          <div className="py-24 flex justify-center"><Loader2 className="w-8 h-8 animate-spin text-emerald-500" /></div>
        ) : tab === 'directory' ? (
          <>
            <div className="relative">
              <Search className="w-4 h-4 text-slate-400 absolute left-3 top-1/2 -translate-y-1/2" />
              <Input
                placeholder="Search by name, email, or role…"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                className="pl-9"
                data-testid="team-search"
              />
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3" data-testid="team-grid">
              {filtered.map((m) => (
                <Card key={m.id} className="hover:shadow-md transition-shadow" data-testid={`member-card-${m.id}`}>
                  <CardContent className="p-4">
                    <div className="flex items-start gap-3">
                      <div className="w-11 h-11 rounded-full bg-gradient-to-br from-teal-100 to-emerald-100 flex items-center justify-center text-teal-700 font-semibold flex-shrink-0">
                        {(m.name || m.email || '?').slice(0, 1).toUpperCase()}
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex flex-wrap items-center gap-1.5">
                          <p className="font-medium text-slate-900 truncate">{m.name || m.email}</p>
                          {m.is_admin && <Crown className="w-3.5 h-3.5 text-amber-500" />}
                          {m.is_manager && !m.is_admin && <ShieldCheck className="w-3.5 h-3.5 text-indigo-500" />}
                        </div>
                        <p className="text-xs text-slate-500 truncate flex items-center gap-1">
                          <Mail className="w-3 h-3" /> {m.email}
                        </p>
                        <div className="flex flex-wrap gap-1.5 mt-1.5">
                          {m.intern_role_label && <Badge variant="outline" className="text-xs bg-emerald-50 border-emerald-300 text-emerald-700">{m.intern_role_label}</Badge>}
                          {m.department && <Badge variant="outline" className="text-xs">{m.department}</Badge>}
                        </div>
                        {m.manager_name && (
                          <p className="text-[11px] text-slate-500 mt-1.5">Reports to <span className="font-medium">{m.manager_name}</span></p>
                        )}
                      </div>
                      {isAdmin && (
                        <Button size="sm" variant="ghost" onClick={() => openManagerDialog(m)} title="Manage reporting line" data-testid={`edit-manager-${m.id}`}>
                          <Edit2 className="w-4 h-4" />
                        </Button>
                      )}
                    </div>
                  </CardContent>
                </Card>
              ))}
              {filtered.length === 0 && <p className="text-slate-500 py-8">No matches.</p>}
            </div>
          </>
        ) : (
          <Card>
            <CardContent className="p-4" data-testid="org-chart">
              {tree.length === 0 ? (
                <p className="text-slate-500 py-8 text-center">No hierarchy set up yet. Admins can assign managers via the directory.</p>
              ) : (
                flattenNodes(tree).map((n) => <OrgRow key={`n-${n.id}`} node={n} />)
              )}
              {unassigned.length > 0 && (
                <div className="mt-6 pt-4 border-t border-dashed border-slate-200">
                  <p className="text-xs uppercase tracking-wider text-slate-500 mb-2 font-semibold">Unassigned</p>
                  {flattenNodes(unassigned).map((n) => <OrgRow key={`u-${n.id}`} node={n} />)}
                </div>
              )}
            </CardContent>
          </Card>
        )}
      </div>

      {/* Manager assignment dialog (admin only) */}
      <Dialog open={managerDialog.open} onOpenChange={(o) => (o ? null : setManagerDialog({ open: false, employee: null, manager_id: '', is_manager: false }))}>
        <DialogContent data-testid="manager-dialog">
          <DialogHeader>
            <DialogTitle>Manage reporting line</DialogTitle>
          </DialogHeader>
          {managerDialog.employee && (
            <div className="space-y-4">
              <p className="text-sm text-slate-600">Editing <strong>{managerDialog.employee.name}</strong></p>
              <div>
                <label className="text-sm font-medium">Reports to</label>
                <select
                  value={managerDialog.manager_id || ''}
                  onChange={(e) => setManagerDialog((d) => ({ ...d, manager_id: e.target.value }))}
                  className="mt-1 w-full px-3 py-2 border border-slate-200 rounded-md text-sm"
                  data-testid="manager-select"
                >
                  <option value="">— No manager —</option>
                  {members.filter((m) => m.id !== managerDialog.employee.id).map((m) => (
                    <option key={m.id} value={m.id}>{m.name} {m.is_admin ? '(Admin)' : m.is_manager ? '(Manager)' : ''}</option>
                  ))}
                </select>
              </div>
              <label className="flex items-center gap-2 text-sm">
                <input
                  type="checkbox"
                  checked={managerDialog.is_manager}
                  onChange={(e) => setManagerDialog((d) => ({ ...d, is_manager: e.target.checked }))}
                  data-testid="is-manager-checkbox"
                />
                <span>Mark this person as a manager (shows on org chart)</span>
              </label>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setManagerDialog({ open: false, employee: null, manager_id: '', is_manager: false })}>Cancel</Button>
            <Button onClick={saveManager} disabled={saving} className="bg-teal-600 hover:bg-teal-700" data-testid="save-manager-btn">
              {saving ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : null}
              Save
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </AdminChrome>
  );
};

export default TeamDirectoryPage;
